import * as React from 'react';
import { composePaths } from '@jsonforms/core';
import { StackLayout, StackItem, Box } from '@leagueplatform/genesis-core';
import { useLocales } from '@web-config-app/core-react';
import { JsonFormsDispatch } from '@jsonforms/react';
import {
  EntityFormArrayControl,
  ArrayPrimitiveGroup,
  ArrayAddButton,
  ArrayActionButtons,
  NumberIcon,
  EmptyArrayState,
  useArrayPrimitiveActions,
  useEntityDetailsProps,
} from '@web-config-app/entity-form';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
  SortableItemRenderProps,
} from '@web-config-app/entity-form';
import { RichTextControlArrayItemProvider } from '../../controls-primitive/rich-text-input-control/rich-text-control-array-item-provider';

export const ArrayPrimitiveContents = ({
  id,
  hint,
  label,
  banner,
  data,
  errors,
  addItem,
  removeItems,
  moveDown,
  moveUp,
  arrayAddLabel,
  hideLabel,
  path,
  schema,
  rootSchema,
  uischemas = [],
  uischema,
  renderers,
  enabled,
  localized,
  inputStatus,
  statusMessage,
  createSortableId,
  SortableItem,
  SortableWrapper,
  registerAfterReorderCallback,
}: EntityFormArrayControlProps) => {
  const entityDetailsProps = useEntityDetailsProps();
  const formPath = entityDetailsProps?.formPath;
  const { currentLocale } = useLocales();

  const {
    childUiSchema,
    handleCreateDefaultValue,
    handleMoveDown,
    handleMoveUp,
    handleRemoveItem,
    getArrayItemCacheKey,
    clearCacheKeys,
    handleLocalizedMoveToPosition,
    isLocalizable,
  } = useArrayPrimitiveActions({
    removeItems,
    moveDown,
    moveUp,
    rootSchema,
    uischemas,
    uischema,
    path,
    schema,
    data,
    formPath,
    localized,
    currentLocale,
  });

  const clearCacheKeysRef = React.useRef(clearCacheKeys);
  const handleLocalizedMoveToPositionRef = React.useRef(
    handleLocalizedMoveToPosition,
  );
  const isLocalizableRef = React.useRef(isLocalizable);

  React.useEffect(() => {
    clearCacheKeysRef.current = clearCacheKeys;
    handleLocalizedMoveToPositionRef.current = handleLocalizedMoveToPosition;
    isLocalizableRef.current = isLocalizable;
  }, [clearCacheKeys, handleLocalizedMoveToPosition, isLocalizable]);

  React.useEffect(() => {
    registerAfterReorderCallback((fromIndex: number, toIndex: number) => {
      clearCacheKeysRef.current();

      if (isLocalizableRef.current) {
        handleLocalizedMoveToPositionRef.current({ fromIndex, toIndex });
      }
    });
  }, [registerAfterReorderCallback]);

  const arrayItemsEmpty = !data || data?.length === 0;

  const cacheKeys = data?.map(
    (_: any, index: number) => getArrayItemCacheKey(index) ?? '',
  );

  return (
    <Box
      css={{
        marginBlockStart: `${hideLabel ? '$none' : '1.25rem'}`,
        marginBlockEnd: '$two',
      }}
    >
      <ArrayPrimitiveGroup
        hint={hint}
        label={label}
        banner={banner}
        hideLabel={hideLabel}
        errors={errors}
        statusMessage={statusMessage}
        inputStatus={inputStatus}
      >
        {arrayItemsEmpty ? (
          <EmptyArrayState item={label} />
        ) : (
          <SortableWrapper>
            <StackLayout role="list" aria-label={label}>
              {/**
               *  We spread data because there is weird behavior when data is undefined and processed through json forms -> it turns it into a "Hole" in the array instead of staying as undefined.
               * This was resulting in behavior when a value was cleared in an input completely  (via backspace or the clear button in input) it also deleted the array item but held its space as a "hole".
               * spreading the data fills in all the holes with undefined values, so the array is always filled with values where it should be.
               */}
              {[...data]?.map((_: any, index: number) => {
                const childPath = composePaths(path, `${index}`);
                const listItemNumber = index + 1;
                const sortableId = createSortableId(index);
                const key = id ? `${id}-${index}` : sortableId;

                return (
                  <SortableItem
                    key={key}
                    id={sortableId}
                    disabled={!enabled}
                    containerCss={{
                      width: '100%',
                      marginBlockStart: '$half',
                      flexGrow: 0,
                    }}
                  >
                    {(
                      dragHandleProps: SortableItemRenderProps['dragHandleProps'],
                    ) => (
                      <StackLayout
                        role="listitem"
                        orientation="horizontal"
                        verticalAlignment="center"
                        spacing="$half"
                        css={{
                          width: '100%',
                        }}
                      >
                        <StackItem>
                          <NumberIcon number={listItemNumber} />
                        </StackItem>
                        <StackItem css={{ width: '100%' }}>
                          <RichTextControlArrayItemProvider
                            cacheKey={cacheKeys?.[index]}
                          >
                            <JsonFormsDispatch
                              schema={schema}
                              uischema={childUiSchema || uischema}
                              path={childPath}
                              key={childPath}
                              renderers={renderers}
                            />
                          </RichTextControlArrayItemProvider>
                        </StackItem>
                        <StackItem>
                          <ArrayActionButtons
                            arrayLength={data.length}
                            itemIndex={index}
                            itemLabel={label}
                            onDelete={handleRemoveItem(path, index)}
                            onMoveDown={handleMoveDown(path, index)}
                            onMoveUp={handleMoveUp(path, index)}
                            disableAllActions={!enabled}
                            dragHandleProps={dragHandleProps}
                          />
                        </StackItem>
                      </StackLayout>
                    )}
                  </SortableItem>
                );
              })}
            </StackLayout>
          </SortableWrapper>
        )}
      </ArrayPrimitiveGroup>
      <ArrayAddButton
        addButtonLabel={arrayAddLabel}
        addItem={addItem}
        path={path}
        createDefaultValue={handleCreateDefaultValue}
        disabled={!enabled}
      />
    </Box>
  );
};

const renderControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayPrimitiveContents {...formContentsProps} />
);

export const ArrayPrimitiveControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={renderControl} />
);
